// Skybox kit: stars, comets, distant black hole, aurora.
// Lifted/cleaned from Phase 1 cel scene. Each function returns a THREE.Object3D
// with a `userData.update(time)` hook driven by the main loop.

import * as THREE from 'three';

export function makeStarfield({ count = 2200, radius = 480 } = {}) {
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  const sizes = new Float32Array(count);
  const palette = [
    new THREE.Color(0xffffff),
    new THREE.Color(0xc7d8ff),
    new THREE.Color(0xffe4b8),
    new THREE.Color(0xfff2c2),
    new THREE.Color(0xb8c8ff),
  ];
  for (let i = 0; i < count; i++) {
    const u = Math.random(), vv = Math.random();
    const theta = 2 * Math.PI * u;
    const phi = Math.acos(2 * vv - 1);
    const r = radius * (0.85 + Math.random() * 0.15);
    positions[i*3+0] = r * Math.sin(phi) * Math.cos(theta);
    positions[i*3+1] = r * Math.cos(phi);
    positions[i*3+2] = r * Math.sin(phi) * Math.sin(theta);
    const c = palette[Math.floor(Math.random() * palette.length)];
    colors[i*3+0] = c.r; colors[i*3+1] = c.g; colors[i*3+2] = c.b;
    sizes[i] = Math.random() < 0.05 ? 2.6 : Math.random() * 1.2 + 0.4;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geo.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
  const mat = new THREE.ShaderMaterial({
    uniforms: { uTime: { value: 0 } },
    vertexShader: `
      attribute float size;
      varying vec3 vColor;
      varying float vSize;
      uniform float uTime;
      void main() {
        vColor = color;
        vSize = size;
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        float tw = 0.7 + 0.3 * sin(uTime * 2.0 + position.x * 0.1 + position.y * 0.3);
        gl_PointSize = size * tw * (300.0 / -mv.z);
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: `
      varying vec3 vColor;
      varying float vSize;
      void main() {
        vec2 uv = gl_PointCoord - 0.5;
        float d = length(uv);
        float a = smoothstep(0.5, 0.0, d);
        float flare = max(
          smoothstep(0.5, 0.0, abs(uv.x) * 8.0) * smoothstep(0.5, 0.0, abs(uv.y) * 1.0),
          smoothstep(0.5, 0.0, abs(uv.y) * 8.0) * smoothstep(0.5, 0.0, abs(uv.x) * 1.0)
        );
        a += flare * 0.5 * step(1.5, vSize);
        if (a < 0.02) discard;
        gl_FragColor = vec4(vColor, a);
      }
    `,
    vertexColors: true, transparent: true, depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const points = new THREE.Points(geo, mat);
  points.userData.update = (t) => { mat.uniforms.uTime.value = t; };
  return points;
}

export function makeBlackHole({ position = new THREE.Vector3(-260, 60, -320) } = {}) {
  const grp = new THREE.Group();
  grp.position.copy(position);
  const core = new THREE.Mesh(new THREE.SphereGeometry(14, 32, 32),
    new THREE.MeshBasicMaterial({ color: 0x000000 }));
  grp.add(core);
  const diskGeo = new THREE.RingGeometry(16, 44, 96, 1);
  const diskMat = new THREE.ShaderMaterial({
    uniforms: { uTime: { value: 0 } },
    vertexShader: `varying vec2 vUv; varying float vR;
      void main() { vUv = uv; vR = length(position.xy);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: `varying vec2 vUv; varying float vR; uniform float uTime;
      void main() {
        float t = (vR - 16.0) / 28.0;
        vec3 hot = vec3(1.0, 0.85, 0.55);
        vec3 mid = vec3(1.0, 0.45, 0.25);
        vec3 cold = vec3(0.55, 0.15, 0.6);
        vec3 c = mix(hot, mid, smoothstep(0.0, 0.4, t));
        c = mix(c, cold, smoothstep(0.5, 1.0, t));
        float angle = atan(vUv.y - 0.5, vUv.x - 0.5);
        float streak = 0.5 + 0.5 * sin(angle * 18.0 + uTime * 1.5 - vR * 0.4);
        c *= 0.55 + 0.45 * streak;
        float a = smoothstep(1.0, 0.6, t) * smoothstep(0.0, 0.1, t);
        gl_FragColor = vec4(c, a);
      }`,
    transparent: true, side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending, depthWrite: false,
  });
  const disk = new THREE.Mesh(diskGeo, diskMat);
  disk.rotation.x = Math.PI / 2 - 0.4;
  disk.rotation.z = 0.2;
  grp.add(disk);
  grp.userData.update = (t) => { diskMat.uniforms.uTime.value = t; disk.rotation.z += 0.001; };
  return grp;
}

export function makeAurora({ palette = [0x49ffd7, 0xff5dc7, 0x6b7dff] } = {}) {
  const grp = new THREE.Group();
  const colors = palette.map(c => new THREE.Color(c));
  for (let i = 0; i < 3; i++) {
    const geo = new THREE.PlaneGeometry(360, 90, 80, 16);
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uColorA: { value: colors[i % colors.length] },
        uColorB: { value: colors[(i + 1) % colors.length] },
        uOffset: { value: i * 1.7 },
      },
      vertexShader: `uniform float uTime; uniform float uOffset;
        varying vec2 vUv; varying float vWave;
        void main() {
          vUv = uv; vec3 p = position;
          float w = sin(p.x * 0.05 + uTime * 0.4 + uOffset) * 12.0
                  + sin(p.x * 0.13 + uTime * 0.9 + uOffset * 2.0) * 4.0;
          p.y += w; vWave = w;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
        }`,
      fragmentShader: `uniform float uTime; uniform vec3 uColorA; uniform vec3 uColorB;
        varying vec2 vUv; varying float vWave;
        void main() {
          float v = smoothstep(0.0, 0.4, vUv.y) * smoothstep(1.0, 0.5, vUv.y);
          float ribbon = 0.5 + 0.5 * sin(vUv.x * 30.0 + uTime * 0.6 + vWave * 0.3);
          vec3 c = mix(uColorA, uColorB, vUv.x + 0.2 * sin(uTime + vUv.y * 4.0));
          float a = v * (0.35 + 0.4 * ribbon);
          gl_FragColor = vec4(c, a * 0.55);
        }`,
      transparent: true, depthWrite: false,
      blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(0, 160 + i * 14, -260);
    mesh.rotation.x = -0.25 + i * 0.05;
    mesh.rotation.z = i * 0.1 - 0.1;
    mesh.userData.mat = mat;
    grp.add(mesh);
  }
  grp.userData.update = (t) => grp.children.forEach(c => { c.userData.mat.uniforms.uTime.value = t; });
  return grp;
}

export function makeComets({ count = 5 } = {}) {
  const grp = new THREE.Group();
  const comets = [];
  for (let i = 0; i < count; i++) {
    const geo = new THREE.PlaneGeometry(36, 1.2);
    const mat = new THREE.ShaderMaterial({
      uniforms: { uColor: { value: new THREE.Color(0xffd9a6) } },
      vertexShader: `varying vec2 vUv; void main() { vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
      fragmentShader: `uniform vec3 uColor; varying vec2 vUv;
        void main() {
          float tail = pow(vUv.x, 2.0);
          float vert = smoothstep(0.0, 0.5, vUv.y) * smoothstep(1.0, 0.5, vUv.y);
          gl_FragColor = vec4(uColor, tail * vert);
        }`,
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    });
    const mesh = new THREE.Mesh(geo, mat);
    grp.add(mesh);
    comets.push({ mesh,
      phase: Math.random() * Math.PI * 2,
      speed: 0.05 + Math.random() * 0.08,
      yOff: 120 + Math.random() * 120,
      zOff: -160 - Math.random() * 160,
      tilt: (Math.random() - 0.5) * 0.6 });
  }
  grp.userData.update = (t) => {
    comets.forEach(c => {
      const p = (t * c.speed + c.phase) % (Math.PI * 2);
      const x = -400 + (p / (Math.PI * 2)) * 800;
      c.mesh.position.set(x, c.yOff - p * 16, c.zOff);
      c.mesh.rotation.z = c.tilt;
    });
  };
  return grp;
}
